import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { Button3D } from '@/components/ui/button-3d';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import slideOrcamentos from '@/components/images/slide-tutorial/01-orcamentos.png';
import slideWhatsapp from '@/components/images/slide-tutorial/03-whatsapp.png';
import slideOffline from '@/components/images/slide-tutorial/06-offline-multiempresa.png';

interface OnboardingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * TRÊS slides, não seis.
 *
 * O carrossel é o que separa a pessoa da primeira tarefa: cada slide a mais é
 * um toque a mais antes de ela conseguir orçar. Ficaram os três que ela usa
 * NESTA sessão — o que o app faz, como o cliente recebe, e a garantia de que
 * funciona no meio da obra sem sinal.
 *
 * Saíram (as imagens seguem no repositório, é só reinserir): ciclo completo,
 * NFS-e e financeiro/agenda. Não são menos importantes — são DEPOIS. Vender
 * contrato e nota fiscal para quem ainda não fez um orçamento é anunciar o
 * segundo andar de uma casa sem porta; esses módulos já se apresentam sozinhos
 * na home e nos selos PRO, no momento em que fazem sentido.
 */
const slides = [
  {
    image: slideOrcamentos,
    title: 'Orçamentos profissionais em minutos',
    description: 'Monte com seus itens e preços e gere um PDF pronto para enviar ao cliente.',
  },
  {
    image: slideWhatsapp,
    title: 'Envie e aprove pelo WhatsApp',
    description: 'Compartilhe o link, o cliente aprova de onde estiver e você acompanha o status.',
  },
  {
    image: slideOffline,
    title: 'Funciona offline, pra mais de uma empresa',
    description: 'Crie orçamentos mesmo sem internet — sincroniza sozinho depois. E gerencie várias empresas ou marcas.',
  },
];

export function OnboardingModal({ open, onOpenChange }: OnboardingModalProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { user, refreshProfile } = useAuth();

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleComplete = async () => {
    // Mark onboarding as seen in the database
    if (user) {
      await supabase
        .from('profiles')
        .update({ has_seen_onboarding: true })
        .eq('id', user.id);
      
      // Refresh profile to update local state
      refreshProfile();
    }
    onOpenChange(false);
  };

  const slide = slides[currentSlide];
  const isLast = currentSlide === slides.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Tela cheia: expande o fundo do modal para ocupar toda a viewport
          (sobrescreve o card centralizado/limitado por largura do DialogContent).
          `justify-center` centraliza o conjunto imagem+texto verticalmente, para a
          imagem não ficar colada no topo e sim logo acima do título. */}
      <DialogContent className="left-0 top-0 translate-x-0 translate-y-0 max-w-none w-screen h-[100dvh] rounded-none sm:rounded-none p-0 gap-0 grid-rows-none flex flex-col justify-center overflow-y-auto bg-white">
        {/* Slide illustration (4:3) */}
        <div className="w-full aspect-[4/3] bg-white flex-none">
          <img
            src={slide.image}
            alt=""
            className="h-full w-full object-cover"
            draggable={false}
          />
        </div>

        {/* Content */}
        <div className="flex-none p-6 pt-4 text-center">
          <h2 className="text-xl font-bold text-foreground mb-2">
            {slide.title}
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            {slide.description}
          </p>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  currentSlide === index
                    ? 'w-6 bg-primary'
                    : 'w-2 bg-muted-foreground/30'
                }`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-3 mt-6">
            {currentSlide > 0 && (
              <Button3D color="gray" onClick={handlePrev} className="flex-1">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button3D>
            )}

            <Button3D
              onClick={handleNext}
              className={currentSlide > 0 ? 'flex-1' : 'w-full'}
            >
              {isLast ? (
                'Começar!'
              ) : (
                <>
                  Próximo
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button3D>
          </div>

          {/* Skip button */}
          {!isLast && (
            <Button
              variant="ghost"
              className="w-full mt-2 text-muted-foreground"
              onClick={handleComplete}
            >
              Pular tutorial
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

